import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight,
  MapPin,
  Bike,
  Car,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Users,
  Zap,
  ChevronDown
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-800 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-900/30 transition-colors"
    >
      <span className="font-medium text-white pr-4">{question}</span>
      <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="px-6 pb-4 text-gray-400">
        {answer}
      </div>
    )}
  </div>
);

const sydneyFAQs = [
  {
    q: "How much can I earn as a delivery rider in Sydney?",
    a: "Sydney delivery riders typically earn between $20-$45 per hour depending on the vehicle type, time of day, and area. E-bike riders average $20-$30/hr, while car drivers in outer suburbs can earn $28-$45/hr during peak times. Weekends and dinner hours (5-9pm) offer the highest earning potential."
  },
  {
    q: "What are the best areas for food delivery in Sydney?",
    a: "The most profitable areas include Sydney CBD (high lunch demand), Surry Hills and Newtown (dense restaurant areas), Bondi and Eastern Suburbs (premium tips), Parramatta (steady demand, less competition), and North Sydney/Chatswood (business districts). Inner city areas work best for bikes, while outer suburbs suit car delivery."
  },
  {
    q: "Do I need a licence to deliver food in Sydney?",
    a: "It depends on your vehicle. E-bikes (pedal-assist, 250W max) require no licence. For scooters of ANY size in NSW, you need a motorcycle licence (R or RE class) — a car licence is not sufficient. For car delivery, a valid driver's licence is required (overseas licences may be accepted on some platforms)."
  },
  {
    q: "How do I start delivering with Uber Eats or DoorDash in Sydney?",
    a: "You need: 1) Valid ID (passport or Australian ID), 2) Work rights in Australia (visa with work permission), 3) Australian bank account, 4) Smartphone, and 5) Appropriate vehicle and licence. The registration process involves document verification and may include a police check (~$49 through the platform). Just Delivery can guide you through the entire process."
  },
  {
    q: "What documents do I need to become a delivery rider in Sydney?",
    a: "Required documents include: proof of identity (passport or government ID), proof of work rights (visa grant notice or VEVO check), driver's licence (if using scooter/motorbike/car), and Australian bank account details. Some platforms may also require a police check certificate."
  },
  {
    q: "Is Sydney a good city for new delivery riders?",
    a: "Yes, Sydney is excellent for new riders due to high demand from its 5+ million population, diverse dining options, and year-round mild weather. The city has strong order volumes throughout the day, from breakfast in the CBD to late-night orders in entertainment districts. Competition exists but there's enough demand for new riders to establish themselves."
  }
];

export default function SydneyGuide() {
  const [openFAQ, setOpenFAQ] = useState(null);

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": sydneyFAQs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#6ABF4B]/10 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <Link to="/how-to-start-delivery-australia" className="inline-flex items-center gap-2 text-[#6ABF4B] hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Australia Guide
          </Link>
          
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#6ABF4B]" />
              <span className="text-[#6ABF4B] font-medium">SYDNEY DELIVERY GUIDE 2026</span>
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              How to Start <span className="text-[#6ABF4B]">Delivery Work</span> in Sydney
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-xl text-gray-400 mb-8 max-w-2xl">
              Everything you need to know about becoming a food delivery rider in Sydney — Australia's largest city and busiest delivery market.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link
                to="/?lang=en#packages"
                className="inline-flex items-center gap-2 bg-[#6ABF4B] text-black px-6 py-3 rounded-lg font-semibold hover:bg-[#5aa83f] transition-colors"
              >
                Get Started in Sydney <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Sydney Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Why Sydney is Great for Delivery Riders</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Largest Market</h3>
              <p className="text-gray-400">With over 5 million residents, Sydney has the highest demand for food delivery in Australia. More orders mean more earning opportunities.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Higher Earning Potential</h3>
              <p className="text-gray-400">Sydney riders typically earn more per delivery due to longer distances and higher base rates in premium suburbs.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Flexible Hours</h3>
              <p className="text-gray-400">Sydney's diverse dining culture means strong demand throughout the day — breakfast in the CBD, lunch rushes, and late-night orders.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Year-Round Weather</h3>
              <p className="text-gray-400">Sydney's mild climate allows for comfortable outdoor work most of the year, unlike some other Australian cities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Areas */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Best Areas for Delivery in Sydney</h2>
          <p className="text-gray-400 mb-8">These areas have the highest order volumes and best earning potential:</p>
          
          <div className="space-y-4">
            {[
              { area: "Sydney CBD", desc: "High lunch demand from office workers. Best for quick bike deliveries." },
              { area: "Surry Hills & Newtown", desc: "Dense restaurant areas with consistent orders, especially evenings." },
              { area: "Bondi & Eastern Suburbs", desc: "Premium suburbs with higher tips and longer delivery distances." },
              { area: "Parramatta", desc: "Growing western hub with less competition and steady demand." },
              { area: "North Sydney & Chatswood", desc: "Business districts with strong lunch and dinner orders." },
            ].map((item, idx) => (
              <div key={idx} className="bg-[#111111] border border-gray-800 rounded-xl p-5 flex items-start gap-4">
                <div className="w-8 h-8 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-black font-bold text-sm">{idx + 1}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-white">{item.area}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Typical Sydney Delivery Earnings</h2>
          <p className="text-gray-400 mb-8">Earnings vary based on vehicle type, hours worked, and delivery zones:</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Bike className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">E-Bike</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$20 – $30/hr</p>
              <p className="text-gray-400 text-sm">Best for CBD and inner suburbs. Low costs, no licence needed.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Bike className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Scooter</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$25 – $38/hr</p>
              <p className="text-gray-400 text-sm">Faster coverage. Requires motorcycle licence (R/RE class) in NSW.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Car className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Car</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$28 – $45/hr</p>
              <p className="text-gray-400 text-sm">Best for outer suburbs and larger orders. Higher fuel costs.</p>
            </div>
          </div>
          
          <p className="text-gray-500 text-sm mt-6 text-center">
            * Earnings are estimates based on typical Sydney delivery data. Actual results vary by demand, time, and experience.
          </p>
        </div>
      </section>

      {/* Sydney-Specific Requirements */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Sydney-Specific Requirements</h2>
          <p className="text-gray-400 mb-8">NSW has specific rules for delivery riders:</p>
          
          <div className="space-y-4">
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">Scooter Licence Requirement</h3>
                  <p className="text-gray-400">In NSW, you need a motorcycle licence (R or RE class) to ride ANY scooter, regardless of engine size. A car licence is NOT sufficient for scooter delivery.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#6ABF4B] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">E-Bike Regulations</h3>
                  <p className="text-gray-400">E-bikes must be pedal-assist only (no throttle) with a maximum power of 250W. No licence required for compliant e-bikes.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <FileText className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">Overseas Licence Conversion</h3>
                  <p className="text-gray-400">Visit Service NSW (55 King St, Sydney CBD) to convert your overseas licence. Some countries have reciprocal agreements for direct conversion.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Delivering in Sydney?</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Just Delivery helps you navigate the onboarding process for Uber Eats and DoorDash. We review your documents, guide you through requirements, and help you start earning faster.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/?lang=en#packages"
              className="inline-flex items-center gap-2 bg-[#6ABF4B] text-black px-8 py-4 rounded-lg font-semibold hover:bg-[#5aa83f] transition-colors"
            >
              Start Your Application <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/how-to-start-delivery-australia"
              className="inline-flex items-center gap-2 bg-transparent border border-gray-600 text-white px-8 py-4 rounded-lg font-semibold hover:border-[#6ABF4B] transition-colors"
            >
              Read Full Australia Guide
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4 text-center">Frequently Asked Questions</h2>
          <p className="text-gray-400 mb-8 text-center">Common questions about delivery work in Sydney</p>
          
          <div className="bg-[#111111] border border-gray-800 rounded-xl overflow-hidden">
            {sydneyFAQs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.q}
                answer={faq.a}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Other Cities Links */}
      <section className="py-12 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold mb-6 text-center">Delivery Guides for Other Cities</h3>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/how-to-start-delivery-melbourne"
              className="text-[#6ABF4B] hover:underline flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" /> Melbourne Guide
            </Link>
            <span className="text-gray-600">|</span>
            <Link
              to="/how-to-start-delivery-brisbane"
              className="text-[#6ABF4B] hover:underline flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" /> Brisbane Guide
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-black border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-gray-500 text-sm text-center">
            Just Delivery is not affiliated with Uber Eats, DoorDash, or any delivery platform. Platform approval decisions are made solely by the respective platforms. We provide onboarding guidance only.
          </p>
        </div>
      </section>
    </div>
    </>
  );
}
